import graphQLClient, {
  type HirerGraphqlClientOptions,
} from '@seek/hirer-graphql-client';
import { getConfig, AuthN } from '@seek/online-identity';
import type { GraphQLError } from 'graphql';

import { type EmployerSiteNames, environments } from 'src/siteConfig';

import type { CreatePageConfig } from './types';

const mockAuthN = {
  login: () => {},
  getToken: () => Promise.resolve('mockToken'),
} as unknown as AuthN;

// FIXME: production config shouldn't need to know about dev environment setup i.e. mockAuthN, graphql routes, etc.
//
// This is here because CM app is built and used `environment` to decide which config to run
// An alternative is to tell which config file to use at build time
// e.g.
//   - `CONFIG=production sku build` for production/staging (uses `config.production.ts`)
//   - `sku build` for dev and e2e (uses `config.ts`)
//   - `sku start` for dev (uses `config.ts`)
// And in each config file, we store the actual config i.e. auth client, graphql route, etc.
export const createPageConfig: CreatePageConfig = ({
  site,
  environment,
  appName,
}) => {
  const isDev = environment === environments.development;
  const isProd = environment === environments.production;
  const authN = isDev
    ? mockAuthN
    : new AuthN(getConfig('seekAnz-hirer', window.location));

  const baseApiPath = isDev ? 'http://localhost:3000' : '';

  // FIXME: it's a bit weird that we are accessing/initliasing TrackJS here.
  // I think it should be passed in as a prop instead, ideally as a generic error logger
  const trackJs = window.TrackJS || null;

  const logGraphQLError: HirerGraphqlClientOptions['logGraphQLError'] = (
    error: GraphQLError,
    operationName?: string,
  ) => {
    if (isProd) {
      trackJs?.track(`GraphQL Error | ${error.message} | ${operationName}`);
    } else {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };
  const logError: HirerGraphqlClientOptions['logError'] = (
    error: unknown,
    operationName?: string,
  ) => {
    if (isProd) {
      trackJs?.track(`Network Error | ${error} | ${operationName}`);
    } else {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  return {
    authN,
    baseApiPath,
    graphClient: createHirerGraphQLClient({
      appName,
      authN,
      site,
      uri: isDev ? `${baseApiPath}/graphql` : '/graphql',
      logError,
      logGraphQLError,
    }),
    trackJs,
  };
};

const createHirerGraphQLClient = ({
  appName,
  site,
  authN,
  uri,
  logError,
  logGraphQLError,
}: {
  appName: string;
  site: EmployerSiteNames;
  authN: AuthN;
  uri?: string;
  logError: HirerGraphqlClientOptions['logError'];
  logGraphQLError: HirerGraphqlClientOptions['logGraphQLError'];
}) => {
  const getToken = async () => {
    const token = await authN.getToken();

    if (typeof token === 'string') {
      return token;
    }

    return null;
  };

  const login = async () => {
    await authN.login();
  };

  return graphQLClient({
    appName,
    site,
    login,
    getAuthToken: getToken,
    renewAuthToken: getToken,
    logMetrics: () => {},
    logError,
    logGraphQLError,
    uri,
  });
};
